import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DashboardLayout from './layout'
import withAuth from '../../services/withAuth'
import {
  Bell,
  Info,
  Wrench,
  Sparkles,
  Globe,
  Filter,
  Search,
} from 'lucide-react'
import newsData from '../../src/data/news.json'

const IconMap = {
  Sparkles,
  Bell,
  Info,
  Wrench,
} as const

type SupportedLanguages = 'fr' | 'en'

interface MultilingualText {
  fr: string
  en: string
}

interface MultilingualTags {
  fr: string[]
  en: string[]
}

interface NewsItem {
  id: string
  icon: keyof typeof IconMap
  title: MultilingualText
  date: string
  description: MultilingualText
  content: MultilingualText
  category: string
  tags: MultilingualTags
  priority: number
  link?: string
}
function formatTextWithLineBreaks(text: string) {
  return text
    .split('\n')
    .map(
      (
        line:
          | string
          | number
          | boolean
          | React.ReactElement<any, string | React.JSXElementConstructor<any>>
          | Iterable<React.ReactNode>
          | React.ReactPortal
          | null
          | undefined,
        index: React.Key | null | undefined,
        array: string | any[],
      ) => (
        <React.Fragment key={index}>
          {line}
          {index !== array.length - 1 && <br />}
        </React.Fragment>
      ),
    )
}
const NewsCard = ({ item }: { item: NewsItem }) => {
  const { i18n } = useTranslation()

  const getCurrentLang = (): SupportedLanguages => {
    const lang = i18n.language.split('-')[0]
    return lang === 'fr' || lang === 'en' ? (lang as SupportedLanguages) : 'en'
  }

  const getLocalizedContent = <T extends MultilingualText | MultilingualTags>(
    content: T,
  ): T[SupportedLanguages] => {
    const currentLang = getCurrentLang()
    return content[currentLang] || content.en
  }

  const Icon = IconMap[item.icon]
  const category = newsData.categories.find(cat => cat.id === item.category)

  return (
    <div className='bg-white rounded-lg p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow'>
      <div className='flex flex-col sm:flex-row items-start gap-4'>
        <div className={`rounded-full p-3 bg-${category?.color}/10`}>
          <Icon className={`h-3 w-3 md:h-6 md:w-6 text-${category?.color}`} />
        </div>
        <div className='flex-1 w-full'>
          <div className='flex flex-col sm:flex-row justify-between items-start mb-2 gap-2'>
            <h3 className='font-semibold text-base md:text-lg text-anthracite'>
              {getLocalizedContent(item.title)}
            </h3>
            <span className='text-xs md:text-sm text-gray-500 whitespace-nowrap'>
              {new Date(item.date).toLocaleDateString(getCurrentLang())}
            </span>
          </div>
          <p className='text-sm md:text-base text-gray-600 mb-3'>
            {formatTextWithLineBreaks(getLocalizedContent(item.description))}
          </p>
          <p className='text-sm md:text-sm text-gray-600 mb-3'>
            {formatTextWithLineBreaks(getLocalizedContent(item.content))}
          </p>
          <div className='flex flex-wrap gap-2'>
            {getLocalizedContent(item.tags).map((tag, index) => (
              <span
                key={index}
                className='px-2 py-1 rounded-full text-xs font-medium bg-grisclair text-anthracite'
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function DashboardHome() {
  const { t, i18n } = useTranslation()
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const currentLang = i18n.language as SupportedLanguages

  const getLocalizedContent = <T extends MultilingualText | MultilingualTags>(
    content: T,
  ): T[SupportedLanguages] => {
    return content[currentLang] || content.en
  }

  const filteredNews = newsData.news
    .map(item => ({
      ...item,
      icon: item.icon as keyof typeof IconMap,
    }))
    .filter(item => {
      const matchesCategory =
        selectedCategory === 'all' || item.category === selectedCategory
      const matchesSearch =
        getLocalizedContent(item.title)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        getLocalizedContent(item.description)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        getLocalizedContent(item.tags).some(tag =>
          tag.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      return matchesCategory && matchesSearch
    })
    .sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })

  return (
    <DashboardLayout title={t('dashboard.personal-space')}>
      <div className='p-4 md:p-6'>
        <div className='mb-6 md:mb-8'>
          <h1 className='text-xl md:text-2xl font-bold text-anthracite mb-2'>
            {t('news.title')}
          </h1>
          <p className='text-sm md:text-base text-gray-600'>
            {t('news.subtitle')}
          </p>
        </div>

        <div className='flex flex-col md:flex-row gap-4 mb-6'>
          <div className='relative flex-1'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5' />
            <input
              type='text'
              placeholder={t('news.search')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='pl-10 pr-4 py-2 w-full text-sm md:text-base rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-redpink/20'
            />
          </div>

          <div className='relative w-full md:w-auto'>
            <Filter className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 md:h-5 md:w-5' />
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              className='pl-10 pr-4 py-2 w-full md:w-auto text-sm md:text-base rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-redpink/20 appearance-none bg-white'
            >
              <option value='all'>{t('news.all_categories')}</option>
              {newsData.categories.map(category => (
                <option key={category.id} value={category.id}>
                  {getLocalizedContent(category.name)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className='grid gap-4 md:gap-6'>
          {filteredNews.map(item => (
            <NewsCard key={item.id} item={item} />
          ))}
          {filteredNews.length === 0 && (
            <div className='text-center py-6 md:py-8 text-gray-500 text-sm md:text-base'>
              {t('news.no_results')}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}

export default withAuth(DashboardHome)
